import {
  AlertTriangle, ArrowDown, ArrowUp, BookOpen, Bug, CalendarClock, CheckSquare, ChevronsUp,
  CircleDot, GitPullRequest, Layers, Lightbulb, ListTree, Minus,
} from 'lucide-react'
import type { Label, Project, Taak, TaakPrioriteit, TaakSoort, TaakStatus } from '../../../gedeeld/types'
import { isTeLaat, prioriteit, sleutelVan, soort, STATUSSEN, tegoed } from '../../lib/opmaak'
import { Avatar } from '../ui'

/* ===========================================================================
 *  De kleine stukjes van een taak
 *
 *  Overal hetzelfde: op het bord, in de lijst, in de backlog en in de lade.
 *  Een bug heeft overal hetzelfde rode kevertje en een urgente taak overal
 *  dezelfde dubbele pijl. Wie een bord scant leest de vorm, niet het woord.
 * =========================================================================== */

export function SoortIcoon({ soort: s, grootte = 15 }: { soort: TaakSoort; grootte?: number }) {
  const k = soort(s)
  const Icoon = s === 'bug' ? Bug : s === 'verhaal' ? BookOpen : s === 'epic' ? Layers
    : s === 'subtaak' ? ListTree : s === 'idee' ? Lightbulb : CheckSquare
  return <span className={`soort-icoon t-${k.kleur}`} title={k.label}><Icoon size={grootte} /></span>
}

export function PrioriteitIcoon({ p, grootte = 15 }: { p: TaakPrioriteit; grootte?: number }) {
  const k = prioriteit(p)
  const Icoon = p === 'urgent' ? ChevronsUp : p === 'hoog' ? ArrowUp : p === 'laag' ? ArrowDown : Minus
  return <span className={`prio-icoon t-${k.kleur}`} title={`Prioriteit: ${k.label}`}><Icoon size={grootte} /></span>
}

export function StatusPil({ status }: { status: TaakStatus }) {
  const s = STATUSSEN.find((x) => x.key === status)!
  return <span className={`status-pil t-${s.kleur}`}><CircleDot size={11} />{s.label}</span>
}

export function Labels({ namen, project }: { namen: string[]; project?: Project | null }) {
  if (!namen.length) return null
  const kleurVan = (n: string) => project?.labels.find((l: Label) => l.naam === n)?.kleur ?? 'info'
  return (
    <span className="labels">
      {namen.map((n) => <span key={n} className={`label t-${kleurVan(n)}`}>{n}</span>)}
    </span>
  )
}

export function Deadline({ taak, kort }: { taak: Pick<Taak, 'deadline' | 'status'>; kort?: boolean }) {
  if (!taak.deadline) return null
  const laat = isTeLaat(taak)
  return (
    <span className={`deadline ${laat ? 'laat' : ''}`} title={new Date(taak.deadline).toLocaleDateString('nl-NL')}>
      {laat ? <AlertTriangle size={12} /> : <CalendarClock size={12} />}
      {kort ? new Date(taak.deadline).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : tegoed(taak.deadline)}
    </span>
  )
}

/** Een kaart op het bord of in de backlog. */
export function TaakKaart({
  taak, projecten, project, onOpen, sleepbaar, onSleep, metProject, subtaken,
}: {
  taak: Taak
  projecten: Project[]
  project?: Project | null
  onOpen: () => void
  sleepbaar?: boolean
  onSleep?: (e: React.DragEvent) => void
  metProject?: boolean
  subtaken?: { klaar: number; totaal: number }
}) {
  const sleutel = sleutelVan(taak, projecten)
  const lijst = taak.checklist
  const af = lijst.filter((c) => c.af).length
  const p = metProject ? projecten.find((x) => x.id === taak.projectId) : null

  return (
    <div
      className={`kaart ${isTeLaat(taak) ? 'laat' : ''} ${taak.status === 'klaar' ? 'klaar' : ''}`}
      draggable={sleepbaar}
      onDragStart={onSleep}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen() }}
      tabIndex={0}
      role="button"
      data-taak={taak.id}
    >
      {p && <span className="kaart-project"><span className={`punt t-${p.kleur}`} />{p.naam}</span>}
      <span className="kaart-titel">{taak.titel}</span>
      {!!taak.labels.length && <Labels namen={taak.labels} project={project ?? p} />}
      <span className="kaart-voet">
        <SoortIcoon soort={taak.soort} grootte={13} />
        {sleutel && <span className="mono stil">{sleutel}</span>}
        <PrioriteitIcoon p={taak.prioriteit} grootte={13} />
        {!!lijst.length && <span className={`kaart-lijst ${af === lijst.length ? 'af' : ''}`}><CheckSquare size={12} />{af}/{lijst.length}</span>}
        {subtaken && subtaken.totaal > 0 && <span className="kaart-lijst"><ListTree size={12} />{subtaken.klaar}/{subtaken.totaal}</span>}
        {taak.githubIssue && <GitPullRequest size={12} className="stil" />}
        <Deadline taak={taak} kort />
        <span className="vul" />
        {taak.punten !== null && <span className="punten" title="Story points">{taak.punten}</span>}
        <Avatar naam={taak.toegewezenNaam} id={taak.toegewezenAan} grootte={20} />
      </span>
    </div>
  )
}
